import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import FeedbackSoportePanel from './FeedbackSoportePanel';

const COLUMNAS = [
  { key: 'abierto', label: 'Nuevos', color: 'red', gradient: 'from-red-500 to-rose-500', icon: 'fa-circle-exclamation', bg: 'bg-red-50', border: 'border-red-200', badge: 'bg-red-500' },
  { key: 'en_proceso', label: 'En Ejecucion', color: 'amber', gradient: 'from-amber-500 to-orange-500', icon: 'fa-wrench', bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-500' },
  { key: 'resuelto', label: 'Completados', color: 'green', gradient: 'from-green-500 to-emerald-500', icon: 'fa-check-circle', bg: 'bg-green-50', border: 'border-green-200', badge: 'bg-green-500' }
];

const PRIORIDAD_CONFIG = {
  baja: { label: 'Baja', dot: 'bg-gray-400' },
  normal: { label: 'Normal', dot: 'bg-blue-500' },
  urgente: { label: 'Urgente', dot: 'bg-red-500' }
};

const CAT_CONFIG = {
  login: { label: 'Acceso', icon: 'fa-key', color: 'text-blue-500 bg-blue-50' },
  pruebas: { label: 'Pruebas', icon: 'fa-file-alt', color: 'text-purple-500 bg-purple-50' },
  chat: { label: 'Chat', icon: 'fa-comments', color: 'text-green-500 bg-green-50' },
  pagos: { label: 'Pagos', icon: 'fa-credit-card', color: 'text-amber-500 bg-amber-50' },
  clases: { label: 'Clases', icon: 'fa-book', color: 'text-indigo-500 bg-indigo-50' },
  cita: { label: 'Cita', icon: 'fa-calendar-check', color: 'text-cyan-500 bg-cyan-50' },
  otro: { label: 'Otro', icon: 'fa-question-circle', color: 'text-gray-500 bg-gray-50' }
};

const SoporteTecnico = () => {
  const { API, user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [respuesta, setRespuesta] = useState('');
  const [respondiendo, setRespondiendo] = useState(false);
  const [dragTicket, setDragTicket] = useState(null);
  const [controlRemoto, setControlRemoto] = useState(null); // {support_url, admin_url, is_preview, push_enviado}
  const [iniciandoControl, setIniciandoControl] = useState(false);

  useEffect(() => { loadTickets(); }, []);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/soporte/tickets`);
      setTickets(response.data || []);
    } catch { toast.error('Error al cargar tickets'); }
    finally { setLoading(false); }
  };

  const moverTicket = async (ticketId, nuevoEstado, conRespuesta = false) => {
    setRespondiendo(true);
    try {
      const params = new URLSearchParams({ estado: nuevoEstado });
      if (conRespuesta && respuesta.trim()) params.append('respuesta', respuesta.trim());
      await axios.put(`${API}/soporte/tickets/${ticketId}/estado?${params.toString()}`);
      toast.success(`Ticket movido a "${COLUMNAS.find(c => c.key === nuevoEstado)?.label}"`);
      setSelectedTicket(null);
      setRespuesta('');
      loadTickets();
    } catch { toast.error('Error al actualizar ticket'); }
    finally { setRespondiendo(false); }
  };

  const handleIniciarVideo = async (ticket) => {
    try {
      const response = await axios.post(`${API}/soporte/videollamada`);
      window.open(response.data.sala_url, '_blank');
      toast.success('Sala creada. El usuario fue notificado.');
    } catch { toast.error('Error al crear videollamada'); }
  };

  const handleIniciarControlRemoto = async (ticket, enviarPush = false) => {
    setIniciandoControl(true);
    try {
      const response = await axios.post(
        `${API}/soporte/tickets/${ticket.id}/iniciar-control-remoto?enviar_push_estudiante=${enviarPush}`
      );
      setControlRemoto(response.data);
      if (response.data.is_preview) {
        toast.info('Solicitud enviada al usuario (preview: sin push real)');
      } else {
        toast.success('Solicitud de soporte remoto enviada al usuario');
      }
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    } finally {
      setIniciandoControl(false);
    }
  };

  const copiarLink = (texto) => {
    navigator.clipboard.writeText(texto);
    toast.success('Link copiado');
  };

  const handleDrop = (columnaKey) => {
    if (dragTicket && dragTicket.estado !== columnaKey) {
      moverTicket(dragTicket.id, columnaKey);
    }
    setDragTicket(null);
  };

  const borrarTicket = async (ticket) => {
    if (!window.confirm(`¿Borrar el ticket de "${ticket.usuario_nombre}" definitivamente? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/soporte/tickets/${ticket.id}`);
      toast.success('Ticket eliminado');
      loadTickets();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.detail || err.message));
    }
  };

  const formatDate = (isoStr) => {
    const d = new Date(isoStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 3600000) return `hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `hace ${Math.floor(diff / 3600000)}h`;
    return d.toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit' });
  };

  const getTicketsByEstado = (estado) => {
    if (estado === 'resuelto') return tickets.filter(t => t.estado === 'resuelto' || t.estado === 'cerrado');
    return tickets.filter(t => t.estado === estado);
  };

  return (
    <div className="space-y-6" data-testid="soporte-tecnico-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <i className="fas fa-headset text-white" />
            </div>
            Soporte Tecnico IBA
          </h1>
          <p className="text-gray-500 text-sm mt-1">Arrastra tickets entre columnas para cambiar estado</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={async () => {
              const t = toast.loading('Re-sincronizando citas con Google Calendar...');
              try {
                const res = await axios.post(`${API}/admin/resincronizar-citas-calendar`);
                const d = res.data;
                toast.dismiss(t);
                if (d.error_fatal) {
                  toast.error(d.error_fatal, { duration: 15000 });
                  return;
                }
                if (d.total === 0) {
                  toast.info('No hay citas futuras pendientes de sincronizar.');
                } else if (d.fallidas > 0) {
                  // Mostrar errores específicos
                  const errores = (d.detalle || [])
                    .filter(x => x.estado === 'fallida' || x.estado === 'error')
                    .map(x => `• ${x.fecha} ${x.hora} (${x.usuario || 's/email'}): ${x.error || 'sin detalle'}`)
                    .join('\n');
                  toast.error(
                    `${d.sincronizadas} OK, ${d.reusadas} ya existían, ${d.fallidas} fallaron:\n\n${errores}`,
                    { duration: 20000, style: { whiteSpace: 'pre-line', maxWidth: '600px' } }
                  );
                } else {
                  toast.success(
                    `✅ ${d.sincronizadas} sincronizadas · ${d.reusadas} ya existían (total ${d.total})`,
                    { duration: 8000 }
                  );
                }
              } catch (e) {
                toast.dismiss(t);
                toast.error(e.response?.data?.detail || 'Error al re-sincronizar con Calendar');
              }
            }}
            variant="outline"
            className="rounded-xl border-2 border-blue-300 text-blue-700 hover:bg-blue-50"
            data-testid="resync-calendar-btn"
            title="Re-sincronizar citas futuras con Google Calendar"
          >
            <i className="fab fa-google mr-2" />Sync Calendar
          </Button>
          <Button
            onClick={loadTickets}
            variant="outline"
            className="rounded-xl border-2"
            data-testid="refresh-tickets-btn"
          >
            <i className="fas fa-sync-alt mr-2" />Actualizar
          </Button>
        </div>
      </div>

      {/* Panel Feedback (solo admin) */}
      {user?.rol === 'admin' && <FeedbackSoportePanel />}

      {/* Kanban Board */}
      {loading ? (
        <div className="text-center py-16">
          <i className="fas fa-spinner fa-spin text-3xl text-indigo-400 mb-3" />
          <p className="text-gray-500">Cargando tickets...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" data-testid="kanban-board">
          {COLUMNAS.map(col => {
            const colTickets = getTicketsByEstado(col.key);
            return (
              <div
                key={col.key}
                className={`rounded-2xl border-2 ${col.border} ${col.bg} min-h-[60vh] flex flex-col`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(col.key)}
                data-testid={`columna-${col.key}`}
              >
                {/* Column header */}
                <div className={`bg-gradient-to-r ${col.gradient} px-5 py-3 rounded-t-xl flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <i className={`fas ${col.icon} text-white`} />
                    <span className="text-white font-bold text-sm">{col.label}</span>
                  </div>
                  <span className="bg-white/25 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    {colTickets.length}
                  </span>
                </div>

                {/* Cards */}
                <div className="p-3 space-y-3 flex-1 overflow-y-auto">
                  {colTickets.length === 0 ? (
                    <div className="text-center py-10 opacity-50">
                      <i className={`fas ${col.icon} text-2xl mb-2`} />
                      <p className="text-xs font-medium">Sin tickets</p>
                    </div>
                  ) : (
                    colTickets.map(ticket => {
                      const cat = CAT_CONFIG[ticket.categoria] || CAT_CONFIG.otro;
                      const prio = PRIORIDAD_CONFIG[ticket.prioridad] || PRIORIDAD_CONFIG.normal;

                      return (
                        <div
                          key={ticket.id}
                          draggable
                          onDragStart={() => setDragTicket(ticket)}
                          onDragEnd={() => setDragTicket(null)}
                          onClick={() => { setSelectedTicket(ticket); setRespuesta(''); }}
                          className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing active:shadow-lg active:scale-[1.02]"
                          data-testid={`ticket-card-${ticket.id}`}
                        >
                          {/* Top row: category + priority + time */}
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${cat.color}`}>
                              <i className={`fas ${cat.icon} mr-1`} />{cat.label}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <span className={`w-2 h-2 rounded-full ${prio.dot}`} title={prio.label} />
                              <span className="text-xs text-gray-400">{formatDate(ticket.created_at)}</span>
                            </div>
                          </div>

                          {/* User */}
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-7 h-7 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {ticket.usuario_nombre?.[0]}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">{ticket.usuario_nombre}</p>
                              <p className="text-xs text-gray-400">{ticket.usuario_rol}</p>
                            </div>
                          </div>

                          {/* Description */}
                          <p className="text-xs text-gray-600 line-clamp-2 mb-3">{ticket.descripcion}</p>

                          {/* Cita info */}
                          {ticket.es_cita && (
                            <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-2.5 py-2 mb-2">
                              <p className="text-xs font-semibold text-cyan-700 mb-1">
                                <i className="fas fa-calendar-check mr-1" />
                                {ticket.cita_fecha} a las {ticket.cita_hora}hs
                              </p>
                              {ticket.sala_url && (
                                <a href={ticket.sala_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
                                  <i className="fas fa-video mr-1" />Unirse a videollamada
                                </a>
                              )}
                            </div>
                          )}

                          {/* Admin response preview */}
                          {ticket.respuesta_admin && (
                            <div className="bg-green-50 rounded-lg px-2.5 py-1.5 mb-2">
                              <p className="text-xs text-green-700 line-clamp-1">
                                <i className="fas fa-reply mr-1" />{ticket.respuesta_admin}
                              </p>
                            </div>
                          )}

                          {/* Actions */}
                          {ticket.estado === 'abierto' && (
                            <div className="flex gap-1.5 pt-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleIniciarVideo(ticket); }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors"
                              >
                                <i className="fas fa-video" />Video
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); moverTicket(ticket.id, 'en_proceso'); }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-amber-50 text-amber-600 text-xs font-medium hover:bg-amber-100 transition-colors"
                              >
                                <i className="fas fa-arrow-right" />Tomar
                              </button>
                            </div>
                          )}
                          {ticket.estado === 'en_proceso' && (
                            <div className="flex gap-1.5 pt-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleIniciarVideo(ticket); }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-emerald-50 text-emerald-600 text-xs font-medium hover:bg-emerald-100 transition-colors"
                              >
                                <i className="fas fa-video" />Video
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); setRespuesta(''); }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-green-50 text-green-600 text-xs font-medium hover:bg-green-100 transition-colors"
                              >
                                <i className="fas fa-check" />Resolver
                              </button>
                            </div>
                          )}
                          {(ticket.estado === 'resuelto' || ticket.estado === 'cerrado') && (
                            <div className="flex pt-1">
                              <button
                                onClick={(e) => { e.stopPropagation(); borrarTicket(ticket); }}
                                className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors"
                                data-testid={`delete-ticket-${ticket.id}`}
                                title="Borrar ticket definitivamente"
                              >
                                <i className="fas fa-trash-alt" />Borrar
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog detalle/respuesta */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md max-h-[85vh] overflow-y-auto p-0">
          {selectedTicket && (() => {
            const cat = CAT_CONFIG[selectedTicket.categoria] || CAT_CONFIG.otro;
            const col = COLUMNAS.find(c => c.key === selectedTicket.estado) || COLUMNAS[0];

            return (
              <>
                {/* Header */}
                <div className={`bg-gradient-to-r ${col.gradient} px-6 pt-6 pb-5 rounded-t-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <i className={`fas ${cat.icon} text-white text-lg`} />
                    </div>
                    <div>
                      <h2 className="text-white font-bold text-lg leading-tight">Ticket #{selectedTicket.id.slice(0, 8)}</h2>
                      <p className="text-white/70 text-xs">{cat.label} · {new Date(selectedTicket.created_at).toLocaleDateString('es-UY', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                  </div>
                </div>

                <div className="px-6 py-5 space-y-5">
                  {/* User */}
                  <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {selectedTicket.usuario_nombre?.[0]}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">{selectedTicket.usuario_nombre}</p>
                      <p className="text-xs text-gray-500">{selectedTicket.usuario_email} · {selectedTicket.usuario_rol}</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <i className="fas fa-align-left text-indigo-500" />
                      Problema reportado
                    </label>
                    <div className="bg-white border-2 border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700">
                      {selectedTicket.descripcion}
                    </div>
                  </div>

                  {/* Previous response */}
                  {selectedTicket.respuesta_admin && (
                    <div className="bg-green-50 border-2 border-green-200 rounded-xl px-4 py-3">
                      <p className="text-sm text-green-800">{selectedTicket.respuesta_admin}</p>
                      <p className="text-xs text-green-600 mt-1">— {selectedTicket.respondido_por}</p>
                    </div>
                  )}

                  {/* Response field */}
                  {!['resuelto', 'cerrado'].includes(selectedTicket.estado) && (
                    <div>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                        <i className="fas fa-pen text-indigo-500" />
                        Tu respuesta
                      </label>
                      <textarea
                        value={respuesta}
                        onChange={(e) => setRespuesta(e.target.value)}
                        placeholder="Escribe una respuesta..."
                        rows={3}
                        className="w-full border-2 border-gray-200 focus:border-indigo-400 rounded-xl px-4 py-3 text-sm transition-colors outline-none resize-none"
                        data-testid="ticket-respuesta-input"
                      />
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="px-6 pb-6 space-y-2">
                  {selectedTicket.estado === 'abierto' && (
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-xl h-11" onClick={() => moverTicket(selectedTicket.id, 'en_proceso', true)} disabled={respondiendo}>
                        <i className="fas fa-wrench mr-2" />Tomar Ticket
                      </Button>
                      <Button className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl h-11 shadow-lg shadow-green-200" onClick={() => moverTicket(selectedTicket.id, 'resuelto', true)} disabled={respondiendo}>
                        {respondiendo ? <i className="fas fa-spinner fa-spin" /> : <><i className="fas fa-check-circle mr-2" />Resolver</>}
                      </Button>
                    </div>
                  )}
                  {selectedTicket.estado === 'en_proceso' && (
                    <Button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-xl h-11 shadow-lg shadow-green-200" onClick={() => moverTicket(selectedTicket.id, 'resuelto', true)} disabled={respondiendo} data-testid="ticket-resolver-btn">
                      {respondiendo ? <><i className="fas fa-spinner fa-spin mr-2" />Enviando...</> : <><i className="fas fa-check-circle mr-2" />Marcar Resuelto</>}
                    </Button>
                  )}
                  {!['cerrado'].includes(selectedTicket.estado) && (
                    <Button variant="outline" className="w-full rounded-xl h-10 border-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => handleIniciarVideo(selectedTicket)}>
                      <i className="fas fa-video mr-2" />Videollamada con {selectedTicket.usuario_nombre?.split(' ')[0]}
                    </Button>
                  )}
                  {!['cerrado'].includes(selectedTicket.estado) && (
                    <Button
                      variant="outline"
                      className="w-full rounded-xl h-10 border-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                      onClick={() => handleIniciarControlRemoto(selectedTicket, false)}
                      disabled={iniciandoControl}
                      data-testid="btn-iniciar-control-remoto"
                    >
                      {iniciandoControl ? <><i className="fas fa-spinner fa-spin mr-2" />Generando…</> : <><i className="fas fa-desktop mr-2" />Iniciar Control Remoto</>}
                    </Button>
                  )}
                  <Button variant="outline" className="w-full rounded-xl h-10" onClick={() => setSelectedTicket(null)}>Cerrar</Button>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modal Control Remoto - confirmación al admin */}
      <Dialog open={!!controlRemoto} onOpenChange={() => setControlRemoto(null)}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-2xl" data-testid="modal-control-remoto">
          {controlRemoto && (
            <div className="bg-white">
              <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <i className="fas fa-desktop text-xl" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg leading-tight">Solicitud Enviada</h3>
                    <p className="text-white/80 text-xs">Esperando ID de TeamViewer del usuario</p>
                  </div>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-900">
                  <p className="font-semibold mb-1">✅ El usuario fue notificado</p>
                  <p className="text-xs">Verá un modal con instrucciones para descargar TeamViewer cuando abra Soporte en IBA Connect.</p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                  <p className="font-semibold mb-2">📋 Próximos pasos:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>El usuario descarga TeamViewer y lo ejecuta</li>
                    <li>Te envía por el chat de soporte el <b>ID</b> y la <b>contraseña</b> que TeamViewer le muestra</li>
                    <li>Vos abrís tu TeamViewer (PC) → ingresás el ID + contraseña → te conectás</li>
                  </ol>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  <i className="fas fa-info-circle mr-1" /><b>Tip:</b> Tu TeamViewer tiene que estar abierto en tu PC con tu cuenta Business activa.
                </div>

                {controlRemoto.is_preview && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-700">
                    <b>Preview:</b> No se mandó push automático al usuario (lo hará en producción).
                  </div>
                )}

                <Button variant="outline" className="w-full rounded-lg h-10" onClick={() => setControlRemoto(null)} data-testid="btn-cerrar-control-remoto">
                  Entendido
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SoporteTecnico;
